import { useState } from 'react'
import { sileo } from 'sileo'

/**
 * Componente de formulario para editar el nombre de una categoría de ticket existente.
 * * **Funcionalidades**:
 * - Valida que el campo no esté vacío antes de enviar.
 * - Detecta si se intentan guardar datos sin cambios respecto al valor original.
 * - Utiliza la librería `sileo` para mostrar notificaciones de advertencia, información y error.
 * - Gestiona el estado de carga (`loading`) para deshabilitar el botón y mostrar un spinner.
 * * @component
 * @param {Object} props - Propiedades del componente.
 * @param {Object} props.category - El objeto de la categoría a editar.
 * @param {number|string} props.category.id - Identificador único de la categoría.
 * @param {string} props.category.name - Nombre actual de la categoría.
 * @param {Function} props.onSave - Función asíncrona que procesa el guardado.
 * Recibe `(id, nuevoNombre)`.
 * * @returns {JSX.Element} Un formulario con validación y manejo de estados de envío.
 */
const TicketCategoryEdit = ({ category, onSave }) => {
  /** @type {[string, Function]} Estado local para el nombre editado, inicializado con el valor actual. */
  const [name, setName] = useState(category?.name || '')

  /** @type {[boolean, Function]} Estado para controlar el bloqueo del botón y el indicador visual de carga. */
  const [loading, setLoading] = useState(false)

  /**
   * Maneja el envío del formulario, aplica validaciones y ejecuta el callback onSave.
   * @async
   * @param {React.FormEvent<HTMLFormElement>} e - Evento de envío del formulario.
   * @returns {Promise<void>}
   */
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!name.trim()) {
      return sileo.warning({
        title: 'Campo requerido',
        description: 'El nombre de la categoría no puede estar vacío.',
      })
    }

    if (name.trim() === category.name) {
      return sileo.info({
        title: 'Sin cambios',
        description: 'No hiciste ninguna modificación.',
      })
    }

    try {
      setLoading(true)
      await onSave(category.id, name.trim())
    } catch (err) {
      console.error(err)
      sileo.error({
        title: 'Error',
        description: 'Ocurrió un problema al guardar.',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-3">
        <label className="tc-label" htmlFor="tc-edit-name">
          Nombre de la categoría
        </label>
        <input
          id="tc-edit-name"
          type="text"
          className="form-control form-control-sm"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ej: Soporte, Facturación, IT..."
          autoFocus
        />
      </div>

      <div className="d-flex justify-content-end">
        <button
          type="submit"
          className="btn btn-sm btn-primary"
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Guardando...
            </>
          ) : (
            <>
              <i className="fa-solid fa-floppy-disk me-2" />
              Guardar cambios
            </>
          )}
        </button>
      </div>

      <style>{`
        .tc-label {
          display: block;
          margin-bottom: 4px;
          font-size: 0.72rem;
          font-weight: 600;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--gm-text-muted);
        }
      `}</style>
    </form>
  )
}

export default TicketCategoryEdit
